//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import HR from '@components/atoms/hr/HR';
import Source from '@components/atoms/source/Source';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { StarRating } from '@components/atoms/star-rating/StarRating';
import {
  BodyErrorText,
  BodySecondaryText,
  BodyText,
  SectionText,
  SubsectionText,
} from '@components/atoms/text/Text';
import IItemMetadata from '@models/IItemMetadata';
import { isDefined } from '@utils/filterPredicates';
import { staticRelativeTime } from '@utils/staticRelativeTime';

export interface UIADetailsReviewProps {
  metadata: IItemMetadata | undefined;
  isError: boolean;
  isLoading: boolean;
}

export const UIADetailsReview = ({
  isError,
  isLoading,
  metadata,
}: UIADetailsReviewProps) => {
  return (
    <ReviewContentDetailsWrapper>
      <SectionText>Review Details</SectionText>
      {isError && (
        <BodyErrorText>
          An unexpected error occurred. Please try again or contact Bazaarvoice
          Support.
        </BodyErrorText>
      )}
      {isLoading ? (
        <CenteredSpinner />
      ) : (
        <>
          {isDefined(metadata) ? (
            <>
              <Source
                userInfo={{
                  networkUserImageUrl:
                    metadata.socialDetails.networkUserImageUrl,
                  networkUsername: metadata.socialDetails.networkUsername,
                  networkUserDisplayName:
                    metadata.socialDetails.networkUserDisplayName,
                }}
              />
              <ReviewContentDetails>
                {isDefined(metadata.socialDetails.title) && (
                  <SubsectionText>
                    {metadata.socialDetails.title}
                  </SubsectionText>
                )}
                {isDefined(metadata.socialDetails.rating) && (
                  <StarRating rating={metadata.socialDetails.rating} />
                )}
                {isDefined(metadata.socialDetails.caption) && (
                  <BodyText>{metadata.socialDetails.caption}</BodyText>
                )}
              </ReviewContentDetails>
              <HR />
              <NetworkDetails>
                <BodyText>
                  {staticRelativeTime(metadata.socialDetails.postedTime, true)}
                </BodyText>
              </NetworkDetails>
            </>
          ) : (
            <BodySecondaryText>No review details available.</BodySecondaryText>
          )}
        </>
      )}
    </ReviewContentDetailsWrapper>
  );
};

const ReviewContentDetailsWrapper = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const ReviewContentDetails = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
  `,
);

const NetworkDetails = styled.div`
  display: flex;
  align-items: center;
`;
